import { useRef, useState } from "react";
import { FadeUp } from "./FadeUp";
import { PhoneFrame } from "./PhoneFrame";
import { CalorieRing } from "./CalorieRing";
import { CheckCircle2, TrendingUp, Dumbbell, Flame } from "lucide-react";

const Screen = ({ children }) => <div className="p-5 pt-9 h-full flex flex-col gap-4">{children}</div>;

const SCREENS = [
  {
    id: "onboarding",
    caption: "Onboarding",
    content: (
      <Screen>
        <p className="text-[#A1A1AA] text-xs">Step 2 of 4</p>
        <p className="font-display text-lg text-white">What's your goal?</p>
        <div className="flex flex-col gap-2">
          {["Fat Loss", "Lean & Toned", "Muscle Gain"].map((g, i) => (
            <div
              key={g}
              className={`rounded-xl px-4 py-3 text-sm border ${
                i === 0 ? "bg-[#C6FF3D] text-black border-[#C6FF3D]" : "bg-[#0A0A0B] text-white border-[#232326]"
              }`}
            >
              {g}
            </div>
          ))}
        </div>
        <div className="mt-auto space-y-2">
          <div className="flex gap-1.5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full ${i < 2 ? "bg-[#C6FF3D]" : "bg-[#232326]"}`} />
            ))}
          </div>
          <div className="bg-[#C6FF3D] text-black text-center font-semibold text-sm rounded-xl py-3">Continue</div>
        </div>
      </Screen>
    ),
  },
  {
    id: "diet-plan",
    caption: "Diet Plan",
    content: (
      <Screen>
        <p className="text-[#A1A1AA] text-xs">Today's Meals</p>
        <p className="font-display text-lg text-white">Diet Plan</p>
        <div className="space-y-2">
          {[
            ["Poha + Peanuts", "320 kcal"],
            ["Dal + Roti + Sabzi", "480 kcal"],
            ["Paneer Bhurji", "440 kcal"],
          ].map(([n, c]) => (
            <div key={n} className="flex justify-between bg-[#0A0A0B] border border-[#232326] rounded-xl px-4 py-2.5 text-xs">
              <span className="text-white">{n}</span>
              <span className="text-[#C6FF3D]">{c}</span>
            </div>
          ))}
        </div>
        <div className="mt-auto">
          <div className="flex h-1.5 rounded-full overflow-hidden mb-2">
            <div style={{ width: "35%" }} className="bg-[#C6FF3D]" />
            <div style={{ width: "45%" }} className="bg-[#A1A1AA]" />
            <div style={{ width: "20%" }} className="bg-[#5c6b2e]" />
          </div>
          <div className="border border-[#232326] text-white text-center text-xs font-semibold rounded-xl py-2.5">
            Swap a Meal
          </div>
        </div>
      </Screen>
    ),
  },
  {
    id: "workout-logger",
    caption: "Workout Logger",
    content: (
      <Screen>
        <div className="flex items-center justify-between">
          <p className="font-display text-lg text-white">Chest Day</p>
          <Dumbbell className="text-[#C6FF3D]" size={18} />
        </div>
        <div className="bg-[#0A0A0B] border border-[#232326] rounded-xl p-3">
          <p className="text-white text-xs mb-2">Bench Press</p>
          {["Set 1 · 60kg x 8", "Set 2 · 62.5kg x 6"].map((s) => (
            <div key={s} className="flex items-center gap-2 text-xs text-[#A1A1AA] py-1">
              <CheckCircle2 size={13} className="text-[#C6FF3D]" /> {s}
            </div>
          ))}
        </div>
        <span className="self-start bg-[#C6FF3D]/10 text-[#C6FF3D] text-xs font-semibold px-3 py-1.5 rounded-full">
          Rest · 00:45
        </span>
        <div className="mt-auto bg-[#C6FF3D] text-black text-center font-semibold text-sm rounded-xl py-3">
          Finish Workout
        </div>
      </Screen>
    ),
  },
  {
    id: "progress",
    caption: "Progress",
    content: (
      <Screen>
        <p className="font-display text-lg text-white">Progress</p>
        <div className="flex justify-center py-1">
          <CalorieRing percent={74} label="74%" sub="to goal" />
        </div>
        <div className="flex items-center gap-2 text-xs text-[#C6FF3D]">
          <TrendingUp size={14} /> 12-day streak
        </div>
        <div className="flex items-end gap-1.5 h-16 mt-auto">
          {[40, 55, 35, 70, 50, 85, 65].map((h, i) => (
            <div key={i} className="flex-1 rounded-t-sm bg-[#C6FF3D]/70" style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="flex items-center gap-1.5 text-[#A1A1AA] text-xs">
          <Flame size={13} className="text-[#C6FF3D]" /> 7-day streak active
        </div>
      </Screen>
    ),
  },
];

const TILT = ["-rotate-3 translate-y-2", "rotate-2 -translate-y-2", "-rotate-2 translate-y-3", "rotate-3 -translate-y-1"];

export const AppScreens = () => {
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / (el.scrollWidth / SCREENS.length));
    setActive(Math.min(idx, SCREENS.length - 1));
  };

  const scrollToIndex = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: (el.scrollWidth / SCREENS.length) * i, behavior: "smooth" });
    setActive(i);
  };

  return (
    <section className="py-14 md:py-20 px-6 bg-[#111113] overflow-hidden">
      <div className="max-w-6xl mx-auto">
        <FadeUp>
          <h2 className="font-display text-4xl sm:text-5xl text-white text-center mb-10 md:mb-14">INSIDE THE APP</h2>
        </FadeUp>
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className="flex gap-8 overflow-x-auto scrollbar-hide px-2 py-2 snap-x snap-mandatory justify-start md:justify-center"
        >
          {SCREENS.map((s, i) => (
            <div key={s.id} className="snap-center flex flex-col items-center gap-4 shrink-0">
              <PhoneFrame className={`hidden md:block transition-transform duration-500 ${TILT[i]}`}>{s.content}</PhoneFrame>
              <PhoneFrame className="md:hidden">{s.content}</PhoneFrame>
              <span data-testid={`app-screen-${s.id}`} className="text-[#A1A1AA] text-sm">
                {s.caption}
              </span>
            </div>
          ))}
        </div>
        <div className="flex md:hidden justify-center gap-1 mt-4">
          {SCREENS.map((s, i) => (
            <button
              key={s.id}
              aria-label={`Go to ${s.caption} screen`}
              onClick={() => scrollToIndex(i)}
              className="p-3 flex items-center justify-center"
            >
              <span
                className={`block rounded-full transition-all duration-300 h-2.5 ${
                  active === i ? "bg-[#C6FF3D] w-6" : "bg-[#333336] w-2.5"
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};
